// Navigation state only: no record snapshots or filter text in URLs.
export const WORK_QUEUE_ORIGIN_PARAM = 'workQueueOrigin';
const STORAGE_PREFIX = 'path.work-queue-origin.v1:';
const MAX_AGE_MS = 8 * 60 * 60 * 1000;
const TOKEN = /^[a-z0-9-]{12,80}$/;
const QUEUE = /^[a-z][a-z0-9-]{0,79}$/;

const storage = () => {
  try { return window.sessionStorage; } catch (_) { return null; }
};
export const workQueueActor = (user, role = user?.role) => {
  const id = user?.userId || user?.staffProfileId;
  return id && role ? `${id}:${role}` : null;
};
export const createWorkQueueOriginId = () => (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
);
export const getWorkQueueOriginId = search => {
  const value = new URLSearchParams(search || '').get(WORK_QUEUE_ORIGIN_PARAM);
  return value && TOKEN.test(value) ? value : null;
};
export const clearWorkQueueOrigins = () => {
  const store = storage();
  if (!store) return;
  try {
    Object.keys(store).filter(key => key.startsWith(STORAGE_PREFIX)).forEach(key => store.removeItem(key));
  } catch (_) { /* Storage may be unavailable. The normal homepage is the fallback. */ }
};
export const saveWorkQueueOrigin = (token, { actor, queueId, filteringText = '', sortingState = {}, scrollTop = 0 }) => {
  const store = storage();
  if (!store || !TOKEN.test(token || '') || !actor || !QUEUE.test(queueId || '')) return false;
  try {
    const now = Date.now();
    const existing = Object.keys(store).filter(key => key.startsWith(STORAGE_PREFIX));
    const retained = [];
    existing.forEach(key => {
      try {
        const entry = JSON.parse(store.getItem(key));
        if (entry.actor !== actor || now - entry.updatedAt > MAX_AGE_MS) store.removeItem(key);
        else retained.push({ key, updatedAt: entry.updatedAt });
      } catch (_) { store.removeItem(key); }
    });
    retained.sort((a, b) => b.updatedAt - a.updatedAt).slice(29).forEach(({ key }) => store.removeItem(key));
    store.setItem(STORAGE_PREFIX + token, JSON.stringify({
      actor, queueId, updatedAt: now,
      filteringText: String(filteringText).slice(0, 2000),
      sortingState: { columnId: typeof sortingState.columnId === 'string' ? sortingState.columnId : null,
        isDescending: Boolean(sortingState.isDescending) },
      scrollTop: Number.isFinite(scrollTop) ? Math.max(0, scrollTop) : 0,
    }));
    return true;
  } catch (_) { return false; }
};
export const readWorkQueueOrigin = (search, actor, allowedQueueIds) => {
  const token = getWorkQueueOriginId(search);
  const store = storage();
  if (!token || !actor || !store) return null;
  try {
    const entry = JSON.parse(store.getItem(STORAGE_PREFIX + token));
    if (!entry || entry.actor !== actor || !QUEUE.test(entry.queueId || '') ||
        !Number.isFinite(entry.updatedAt) || Date.now() - entry.updatedAt > MAX_AGE_MS ||
        typeof entry.filteringText !== 'string' || !entry.sortingState ||
        (entry.sortingState.columnId !== null && typeof entry.sortingState.columnId !== 'string') ||
        typeof entry.sortingState.isDescending !== 'boolean' || !Number.isFinite(entry.scrollTop) || entry.scrollTop < 0 ||
        (allowedQueueIds && !allowedQueueIds.includes(entry.queueId))) return null;
    return { ...entry, token };
  } catch (_) { return null; }
};
export const withWorkQueueOrigin = (path, token) => {
  if (!path || !/^\/(?:application-case|cases)\/\d+(?:[?#]|$)/.test(path) || !TOKEN.test(token || '')) return path;
  const url = new URL(path, 'https://path.invalid');
  url.searchParams.set(WORK_QUEUE_ORIGIN_PARAM, token);
  return url.pathname + url.search + url.hash;
};
// Retain origin only within the same case journey, never across unrelated records.
export const carryWorkQueueOrigin = (path, location) => {
  const sourceCase = location?.pathname?.match(/^\/(?:application-case|cases)\/(\d+)(?:\/|$)/)?.[1];
  const targetCase = path?.match(/^\/(?:application-case|cases)\/(\d+)(?:[?#]|$)/)?.[1];
  return sourceCase && sourceCase === targetCase
    ? withWorkQueueOrigin(path, getWorkQueueOriginId(location.search)) : path;
};
export const workQueueReturnPath = origin => (
  origin?.token && QUEUE.test(origin.queueId || '') && TOKEN.test(origin.token)
    ? `/?queue=${encodeURIComponent(origin.queueId)}&${WORK_QUEUE_ORIGIN_PARAM}=${encodeURIComponent(origin.token)}`
    : '/'
);
